import { apiFetch } from "../../compare/api/helpers";
import {
    parseInterimRecommendedCover,
    parseFinalRecommendedCover,
    parseQuestion,
    parseDisclaimerResults
} from "./mappers/DigitalAdviceMapper";
import { HttpMethod } from "../../compare/enums/HttpMethod";
import {
    IApiDigitalAdviceAnswer,
    IApiDigitalAdviceQuestion,
    IApiDisclaimerResults,
    IApiFinalRecommendedCoverResults,
    IApiInterimRecommendedCoverResults
} from "../interfaces/IApiDigitalAdviceQuestion";
import {
    IDigitalQuestion,
    IDisclaimerResults,
    IFinalRecommendedCoverResults,
    IInterimRecommendedCoverResults,
    IRecommendedCover
} from "../interfaces/IDigitalAdviceData";

interface IUserSessionParams {
    sessionUserId: string;
    visitorId: string;
}

export const getInterimRecommendedCover = async (
    params: IUserSessionParams
): Promise<IInterimRecommendedCoverResults> => {
    return apiFetch<IApiInterimRecommendedCoverResults>(
        `/api/digital-advice/interim-recommended-cover?sessionUserId=${params.sessionUserId}&visitorId=${params.visitorId}`,
        {
            method: HttpMethod.Get
        }
    ).then((res) => parseInterimRecommendedCover(res));
};

export const getFinalRecommendedCover = async (
    params: IUserSessionParams
): Promise<IFinalRecommendedCoverResults> => {
    return apiFetch<IApiFinalRecommendedCoverResults>(
        `/api/digital-advice/final-recommended-cover?sessionUserId=${params.sessionUserId}&visitorId=${params.visitorId}`,
        {
            method: HttpMethod.Get
        }
    ).then((res) => parseFinalRecommendedCover(res));
};

interface IChangeFrequency extends IUserSessionParams {
    isPrimaryAdult: boolean;
    recommendedCoverType: string;
    frequency: string;
}

export const getRecommendedCoverChangeFrequency = async (
    params: IChangeFrequency
): Promise<IRecommendedCover> => {
    return apiFetch<IRecommendedCover>(
        `/api/digital-advice/recommended-cover/change-frequency?sessionUserId=${
            params?.sessionUserId || ""
        }&visitorId=${params?.visitorId || ""}&isPrimaryAdult=${
            params?.isPrimaryAdult || ""
        }&recommendedCoverType=${params?.recommendedCoverType || ""}&frequency=${
            params?.frequency || ""
        }`,
        {
            method: HttpMethod.Get
        }
    );
};

export const getSessionId = async (): Promise<IUserSessionParams> => {
    return apiFetch<IUserSessionParams>(`/api/digital-advice/begin`, {
        method: HttpMethod.Get
    });
};

interface IGetDAPQuestion {
    sessionUserId: string;
    questionId?: number;
    orderBy?: number;
}

export const getDAPEachQuestion = async (
    data: IGetDAPQuestion
): Promise<IDigitalQuestion> => {
    const { questionId, orderBy, sessionUserId } = data;
    let queryParams = "";
    if (questionId) {
        queryParams = `?sessionUserId=${sessionUserId}&questionId=${questionId}`;
    } else {
        queryParams = `?sessionUserId=${sessionUserId}&orderBy=${orderBy}`;
    }

    return apiFetch<IApiDigitalAdviceQuestion>(
        `/api/digital-advice/question${queryParams}`,
        {
            method: HttpMethod.Get
        }
    ).then((res) => parseQuestion(res));
};

export const setQuestionAnswer = async (
    data: IApiDigitalAdviceAnswer
): Promise<IDigitalQuestion> => {
    const { questionId, sessionUserId, questionOptionId, textValue } = data;
    let queryParams = "";
    if (questionOptionId) {
        queryParams = `&questionOptionId=${questionOptionId}`;
    } else {
        queryParams = `&textValue=${textValue}`;
    }
    return apiFetch<IApiDigitalAdviceQuestion>(
        `/api/digital-advice/question?questionId=${questionId}&sessionUserId=${sessionUserId}${queryParams}`,
        {
            method: HttpMethod.Post
        }
    ).then((res) => parseQuestion(res));
};

export const deleteReponse = async (data: IApiDigitalAdviceAnswer): Promise<boolean> => {
    const { questionId, sessionUserId } = data;
    return apiFetch<boolean>(
        `/api/digital-advice/response?sessionUserId=${sessionUserId}&questionId=${questionId}`,
        {
            method: HttpMethod.Delete
        }
    );
};

interface IGenerateQuoteGroupProps extends IUserSessionParams {
    frequency?: string;
    category?: string;
}

export const generateQuoteGroup = async (
    params: IGenerateQuoteGroupProps
): Promise<string> => {
    const { sessionUserId, visitorId, frequency, category } = params;
    let queryParams = "";
    if (frequency) {
        queryParams = `&frequency=${frequency}`;
    }
    if (category) {
        queryParams += `&category=${category}`;
    }

    return apiFetch<string>(
        `/api/digital-advice/generate-quote-group?sessionUserId=${sessionUserId}&visitorId=${visitorId}${queryParams}`,
        {
            method: HttpMethod.Post
        }
    );
};

export const generateSOAPDF = async (
    params: IUserSessionParams
): Promise<ArrayBuffer> => {
    const { sessionUserId, visitorId } = params;
    return apiFetch<ArrayBuffer>(
        `/api/digital-advice/statement-of-advice/download?sessionUserId=${sessionUserId}&visitorId=${visitorId}`,
        {
            method: HttpMethod.Get
        }
    );
};

export const quoteModifiedWarning = async (sessionUserId: string): Promise<boolean> => {
    return apiFetch<boolean>(
        `/api/digital-advice/quote-modified-warning?sessionUserId=${sessionUserId}`,
        {
            method: HttpMethod.Put
        }
    );
};

export const sendStatementOfAdvice = async (
    params: IGenerateQuoteGroupProps
): Promise<boolean> => {
    const { sessionUserId, visitorId } = params;
    return apiFetch<boolean>(
        `/api/digital-advice/statement-of-advice/send?sessionUserId=${sessionUserId}&visitorId=${visitorId}`,
        {
            method: HttpMethod.Post
        }
    );
};

interface IFallbackDetails {
    sessionId: string;
    name: string;
    email: string;
    mobile: string;
}

export const fallbackDetails = async (params: IFallbackDetails): Promise<boolean> => {
    const { sessionId, name, email, mobile } = params;
    return apiFetch<boolean>(
        `/api/digital-advice/fallback-details?sessionId=${sessionId}&name=${name}&email=${email}&mobile=${mobile}`,
        {
            method: HttpMethod.Post
        }
    );
};

export const getRecommendationDisclaimer = async (
    params: IUserSessionParams
): Promise<IDisclaimerResults> => {
    const { sessionUserId, visitorId } = params;

    return apiFetch<IApiDisclaimerResults>(
        `/api/digital-advice/recommendation-disclaimer?sessionUserId=${sessionUserId}&visitorId=${visitorId}`,
        {
            method: HttpMethod.Get
        }
    ).then((res) => parseDisclaimerResults(res));
};

interface IDisclaimerParams {
    sessionUserId: string;
    understoodSuitability: boolean;
    understoodReplacement: boolean;
    recommendationEmail: string;
    isDone: boolean;
}

export const setRecommendationDisclaimer = async (
    params: IDisclaimerParams
): Promise<boolean> => {
    const bodyParams = {
        ...params,
        sessionId: params.sessionUserId
    };

    return apiFetch<boolean>(`/api/digital-advice/recommendation-disclaimer`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(bodyParams)
    });
};

interface ICallbackDetails extends IUserSessionParams {
    name: string;
    email: string;
    mobile: string;
}

export const callbackDetails = async (params: ICallbackDetails): Promise<boolean> => {
    const bodyParams = {
        ...params,
        sessionId: params.sessionUserId
    };
    return apiFetch<boolean>(`/api/digital-advice/callback-details`, {
        method: HttpMethod.Post,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify(bodyParams)
    });
};
